import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { HomeHeroCaptionAdminView, HomeHeroCaptionRequest } from '../types/cms';

// QA-102: 메인 히어로 헤드라인 캡션 (catalog admin, V112). 싱글톤 — id 없는 단일 리소스.
//   PUT 은 writeAllowlist 등록 경로(/admin/v1/cms/home-hero-caption) → Idempotency-Key 필수.
const BASE = '/admin/v1/cms/home-hero-caption';

/** 미설정(아직 BE 시드 없음) 상태에서 폼이 안전하게 비도록 하는 빈 기본값. */
const EMPTY_CAPTION: HomeHeroCaptionAdminView = {
  textBefore: '',
  highlightWord: '',
  textAfter: '',
  highlightColor: null,
  isActive: true,
};

const fetchHeroCaption = async (): Promise<HomeHeroCaptionAdminView> => {
  const res = await adminFetch(BASE);
  // 아직 시드되지 않은 환경(204/404)에서는 빈 캡션으로 폼을 초기화한다.
  if (res.status === 204 || res.status === 404) return EMPTY_CAPTION;
  if (!res.ok) throw new Error(`home-hero-caption fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: HomeHeroCaptionAdminView | null };
  return env.data ?? EMPTY_CAPTION;
};

export const homeHeroCaptionQueryKey = () => ['admin', 'home-hero-caption'] as const;

export const useHomeHeroCaptionQuery = () =>
  useQuery({ queryKey: homeHeroCaptionQueryKey(), queryFn: fetchHeroCaption });

export const useHomeHeroCaptionMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: HomeHeroCaptionRequest): Promise<HomeHeroCaptionAdminView> => {
      const res = await adminFetch(BASE, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', BASE, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`home-hero-caption save failed: ${res.status}`);
      const env = (await res.json()) as { data?: HomeHeroCaptionAdminView | null };
      return env.data ?? { ...body };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeHeroCaptionQueryKey() });
    },
  });
};
